/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useMemo,
  Fragment,
  useRef
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useHundredBricksAPI from '@lib/hundredBricksAPI/useHundredBricksAPI';
import { useAuth } from '@lib/authentication';
import { useProfile } from '@lib/profile';
import { useErrorReporter } from '@lib/errorReporter';
import { LocationBlockedModal } from '@views/Login/components';
import { InactiveAccount, InvestorIsNotLocked } from '@lib/hundredBricksAPI/authentication';
import { Paragraph, Subtitle } from '@components/Styled';
import { useForm, useGeolocation, useRequest } from '@hooks';
import {
  Button,
  InvestorLocationModal,
  OneTimePassword,
  OTPAlert
} from '@components';
import { OTPErrorHandler } from '@components/OTPAlert';

import { ButtonGroup, UnlockLayout, VPNErrorMessage } from './InactiveAccountUnlock.style';

const unlockSchema = {
  code: {
    required: true
  }
};

const InactiveAccountUnlock = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const form = useForm({ schema: unlockSchema });
  const api = useHundredBricksAPI();
  const errorReporter = useErrorReporter();
  const { getCurrentPosition, getGeolocationStatus } = useGeolocation();
  const { profile } = useProfile();
  const [verificationCode, setVerificationCode] = useState();
  const [showVPNMessage, setShowVPNMessage] = useState(false);
  const {
    error,
    isLoading,
    token,
    unlockInactiveAccount
  } = useAuth();

  const [
    unlockCodeResponse,
    isSendingUnlockCode,
    unlockCodeError,
    sendUnlockCode
  ] = useRequest(api.authentication.sendInactiveAccountToken);

  const [displayLocationLockedModal, setDisplayLocationLockedModal] = useState(false);
  const [showInvestorLocationModal, setShowInvestorLocationModal] = useState(false);
  const [isApprovingLocation, setIsApprovingLocation] = useState(false);
  const [isVisibleAlert, setIsVisibleAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');

  const alertTimeRef = useRef(null);

  const handleSendEmailUnlockCode = () => {
    sendUnlockCode(searchParams.get('email'), 'email');
    form.removeError('code');
  };

  const handleUnlockInvestor = async () => {
    getCurrentPosition(coords => {
      const { latitude, longitude } = coords;

      unlockInactiveAccount(
        {
          tokenCode: verificationCode,
          email: searchParams.get('email'),
          latitude,
          longitude
        }
      );
    }, () => {
      setIsApprovingLocation(false);
      setDisplayLocationLockedModal(true);
    });
  };

  const handleVerificationCodeChange = oneTimeCode => {
    setVerificationCode(oneTimeCode);

    form.removeError('code');
  };

  const verificationCodeIsEmpty = useMemo(
    () => verificationCode?.length === 6,
    [verificationCode]
  );

  const acceptShareLocation = () => {
    setShowInvestorLocationModal(false);
    handleUnlockInvestor();
  };

  const cancelShareLocation = () => {
    setShowInvestorLocationModal(false);
    setIsApprovingLocation(false);
  };

  const onSubmit = async () => {
    setShowVPNMessage(false);
    setIsApprovingLocation(true);

    const geolocationStatus = await getGeolocationStatus();

    if (geolocationStatus === 'prompt') {
      setShowInvestorLocationModal(true);
    } else if (geolocationStatus === 'denied') {
      setDisplayLocationLockedModal(true);
      setIsApprovingLocation(false);
    } else if (geolocationStatus === 'granted') {
      handleUnlockInvestor();
    }
  };

  const showAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setIsVisibleAlert(true);

    if (type === 'SUCCESS' || type === 'WARNING') {
      alertTimeRef.current = setTimeout(() => setIsVisibleAlert(false), 5000);
    }
  };

  const handleTryAgain = () => {
    handleSendEmailUnlockCode();
    setIsVisibleAlert(false);
  };

  const VPNError = apiError => {
    if (apiError.code === 'INVALID_USER_CONNECTION') {
      setShowVPNMessage(true);

      return true;
    }

    return false;
  };

  const handleErrors = OTPErrorHandler({
    form,
    showAlert,
    errorReporter,
    canResendErrorCode: 'CANNOT_RESEND_TOKEN',
    onExtraError: VPNError
  });

  useEffect(() => {
    if (!isSendingUnlockCode && !unlockCodeResponse && !unlockCodeError) {
      handleSendEmailUnlockCode();
    }
  }, [isSendingUnlockCode, unlockCodeResponse, unlockCodeError]);

  useEffect(() => {
    if (unlockCodeResponse && !unlockCodeError) {
      showAlert(
        'Se ha enviado el código exitosamente.',
        'SUCCESS'
      );
    } else if (unlockCodeError && !(unlockCodeError instanceof InvestorIsNotLocked)) {
      handleErrors(unlockCodeError.cause);
    }
  }, [unlockCodeResponse, unlockCodeError]);

  useEffect(() => {
    if (token && !error && profile) {
      navigate('/marketplace');
    } else if (error && !(error instanceof InactiveAccount)) {
      handleErrors(error.cause);
      setIsApprovingLocation(false);
    }
  }, [token, error, profile]);

  useEffect(() => () => {
    if (alertTimeRef.current) {
      clearTimeout(alertTimeRef.current);
    }
  }, []);

  return (
    <Fragment>
      <OTPAlert
        isVisible={isVisibleAlert}
        alertType={alertType}
        alertMessage={alertMessage}
        onTryAgain={handleTryAgain}
      />

      <UnlockLayout>
        <Subtitle>Confirma tu identidad</Subtitle>

        {
          !(unlockCodeError && unlockCodeError instanceof InvestorIsNotLocked) && (
            <Fragment>
              <Paragraph>
                Para iniciar sesión, ingresa tu código de seguridad temporal.
              </Paragraph>

              <OneTimePassword
                error={{ message: form.errors.code }}
                showVerifyButton={false}
                resendOptions={['email']}
                onChange={handleVerificationCodeChange}
                onSendCode={handleSendEmailUnlockCode}
              />

              <ButtonGroup>
                <Button
                  color="secondary"
                  onClick={() => navigate('/login')}
                >
                  Regresar
                </Button>

                <Button
                  color="primary"
                  isLoading={isLoading || isApprovingLocation}
                  disabled={!verificationCodeIsEmpty}
                  onClick={onSubmit}
                >
                  Iniciar sesión
                </Button>
              </ButtonGroup>

              {Boolean(showVPNMessage) && (
                <VPNErrorMessage>
                  Tenemos un inconveniente al tratar de iniciar tu sesión.
                  ¿Tienes activa una VPN o un proxy?
                  Desactívala y vuelve a intentarlo.
                </VPNErrorMessage>
              )}
            </Fragment>
          )
        }

        {
          (unlockCodeError && unlockCodeError instanceof InvestorIsNotLocked) && (
            <Fragment>
              <Paragraph>
                Tu cuenta no se encuentra bloqueada. Por favor, inicia sesión para continuar.
              </Paragraph>

              <Button
                type="button"
                onClick={() => { navigate('/login'); }}
              >
                Ir a iniciar sesion
              </Button>
            </Fragment>
          )
        }

        <InvestorLocationModal
          isOpen={showInvestorLocationModal}
          onConfirm={acceptShareLocation}
          onCancel={cancelShareLocation}
        />

        <LocationBlockedModal
          isOpen={displayLocationLockedModal}
          onConfirm={() => setDisplayLocationLockedModal(false)}
        />
      </UnlockLayout>
    </Fragment>
  );
};

export default InactiveAccountUnlock;
